import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './CommissionTracker';
import { AuthProvider } from './auth/AuthContext';
import { SubscriptionProvider } from './billing/SubscriptionContext';
import { initFirstPromoter, captureFprFromUrl } from './lib/firstpromoter';
import { initSentry, captureError } from './lib/sentry';
import { startUpdateNotifier } from './lib/updateNotifier';
import ConsentSignPage from './consent/ConsentSignPage';
import SoaSignPage from './consent/SoaSignPage';
import IntakePortal from './public/IntakePortal';
import LandingPageView from './public/LandingPageView';
import MemberRedirect from './public/MemberRedirect';
import { AriaProvider } from './aria/AriaContext';
import AriaShell from './aria/AriaShell';

// Init Sentry BEFORE any React mount so a crash during initial render
// (like the setActiveClientKey ReferenceError that bricked the app) is
// captured with a full stack — not just observed in the browser console.
// No-op if REACT_APP_SENTRY_DSN is unset.
initSentry();

// Watch for new deploys so a long-open tab never silently runs stale JS.
// Shows a "New version available - Refresh" pill only after a newer build
// ships; invisible on first load. Fail-soft: never blocks app boot.
startUpdateNotifier();

// URL-based public route. When the client opens /consent/<token>
// or /soa/<token> from the SMS / email link, we render the standalone
// signing page instead of the full CRM. No AuthProvider — the page
// is public.
function getPublicRoute() {
  if (typeof window === 'undefined') return null;
  const p = window.location.pathname || '';
  const consent = /^\/consent\/([A-Za-z0-9_-]{8,128})\/?$/.exec(p);
  if (consent) return { kind: 'consent', token: consent[1] };
  const soa = /^\/soa\/([A-Za-z0-9_-]{8,128})\/?$/.exec(p);
  if (soa)     return { kind: 'soa', token: soa[1] };
  // Take-It-Home QR intake — public consumer destination. Opaque token only.
  const intake = /^\/intake\/([A-Za-z0-9_-]{8,128})\/?$/.exec(p);
  if (intake)  return { kind: 'intake', token: intake[1] };
  // Subscriber landing page — public /p/<page-slug>/<subscriber-handle>.
  // The page slug (group 1) is the unique lookup key; the optional trailing
  // handle is the subscriber's name for branding ("our domain, name at the end").
  const page = /^\/p\/([A-Za-z0-9_-]{2,80})(?:\/[A-Za-z0-9_-]{1,60})?\/?$/.exec(p);
  if (page)    return { kind: 'landing', token: page[1] };
  // Carrier portal secure redirect — /member/<token>.
  const member = /^\/member\/([A-Za-z0-9_-]{8,128})\/?$/.exec(p);
  if (member)  return { kind: 'member', token: member[1] };
  return null;
}

// Root error boundary. In production, an unhandled render error normally
// leaves the page completely blank — useless for debugging. This boundary
// catches the crash and prints the error message + stack + relevant env
// state straight onto the page so the issue is visible without devtools.
class RootErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    this.setState({ info });
    // eslint-disable-next-line no-console
    console.error('[RootErrorBoundary] React render crash:', error, info);
    // Report to Sentry. Boundary is the last-resort catch — anything
    // that reaches here would otherwise show the friendly "something
    // went wrong" screen and be invisible in production.
    try {
      captureError(error, {
        boundary: 'root',
        component_stack: info?.componentStack || '',
      });
    } catch { /* never throw from an error boundary */ }
  }
  render() {
    if (!this.state.error) return this.props.children;
    const e = this.state.error;
    // In production: friendly message only. No stack, no env presence
    // flags, no URL — all of which were leaking infrastructure detail
    // to end users (BUG-009). In dev: everything, so a developer can
    // diagnose without opening DevTools.
    const isProd = process.env.NODE_ENV === 'production';

    if (isProd) {
      // BUG-009: friendly message only. No stack, no env presence flags,
      // no URL — the dev view leaks infra detail we don't want in the
      // prod boundary.
      // Two escape hatches, not one — "Reload" alone was useless when
      // the crash was caused by a corrupted localStorage entry (the
      // reload just re-hydrated the same broken state, re-crashed the
      // same way). "Reset & sign in again" nukes cached client state so
      // the user can escape a stuck-crash loop without contacting
      // support. Auth tokens are also cleared so they land on login.
      const resetAndReload = () => {
        try {
          // Clear anything that could re-crash on rehydrate.
          // Keep the format-guard broad: sb-* (Supabase auth), crm:*
          // (per-user app state), commission_tracker_* (legacy keys),
          // aca:*, workflow:*.
          for (let i = window.localStorage.length - 1; i >= 0; i--) {
            const k = window.localStorage.key(i);
            if (!k) continue;
            if (
              k.startsWith('sb-') ||
              k.startsWith('crm:') ||
              k.startsWith('commission_tracker') ||
              k.startsWith('aca:') ||
              k.startsWith('workflow:') ||
              k.includes('supabase.auth.')
            ) {
              window.localStorage.removeItem(k);
            }
          }
          try { window.sessionStorage.clear(); } catch { /* ignore */ }
        } catch { /* ignore — reload is still fine */ }
        window.location.reload();
      };
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: '#F8FAFC', color: '#0F172A', fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif' }}>
          <div style={{ maxWidth: 520, textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>·</div>
            <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Something went wrong</h1>
            <p style={{ fontSize: 14, color: '#475569', marginBottom: 20, lineHeight: 1.6 }}>
              Our team has been notified. Try Reload first. If the same error keeps happening after reload, tap Reset &amp; sign in again — that clears cached data on this device and takes you to the login screen so you can start clean. Your cloud-saved work is safe.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => window.location.reload()} style={{ background: '#0F172A', color: 'white', border: 'none', padding: '10px 18px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>Reload</button>
              <button onClick={resetAndReload} style={{ background: 'white', color: '#0F172A', border: '1px solid #CBD5E1', padding: '10px 18px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>Reset &amp; sign in again</button>
            </div>
          </div>
        </div>
      );
    }

    // Dev / staging — full diagnostic.
    const env = {
      hasSupabaseUrl: !!process.env.REACT_APP_SUPABASE_URL,
      hasSupabaseKey: !!process.env.REACT_APP_SUPABASE_ANON_KEY,
      nodeEnv: process.env.NODE_ENV,
      url: typeof window !== 'undefined' ? window.location.href : '',
    };
    return (
      <div style={{ minHeight: '100vh', padding: 24, background: '#0F172A', color: '#E2E8F0', fontFamily: 'ui-monospace, monospace', lineHeight: 1.5 }}>
        <div style={{ maxWidth: 920, margin: '0 auto' }}>
          <div style={{ fontSize: 12, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 2 }}>Render crash caught by root error boundary (dev only)</div>
          <h1 style={{ fontSize: 22, color: '#FECACA', marginTop: 8, marginBottom: 16, fontFamily: 'system-ui, sans-serif' }}>
            {e && e.name ? `${e.name}: ` : ''}{e && e.message ? e.message : String(e)}
          </h1>
          <div style={{ background: '#1E293B', border: '1px solid #334155', borderRadius: 8, padding: 12, fontSize: 12, color: '#CBD5E1' }}>
            <div style={{ color: '#94A3B8', marginBottom: 6 }}>Environment</div>
            <div>NODE_ENV: <strong style={{ color: '#FEF08A' }}>{env.nodeEnv || 'unset'}</strong></div>
            <div>REACT_APP_SUPABASE_URL present: <strong style={{ color: env.hasSupabaseUrl ? '#86EFAC' : '#FCA5A5' }}>{String(env.hasSupabaseUrl)}</strong></div>
            <div>REACT_APP_SUPABASE_ANON_KEY present: <strong style={{ color: env.hasSupabaseKey ? '#86EFAC' : '#FCA5A5' }}>{String(env.hasSupabaseKey)}</strong></div>
            <div style={{ marginTop: 8, color: '#94A3B8' }}>URL</div>
            <div style={{ wordBreak: 'break-all' }}>{env.url}</div>
          </div>
          {e && e.stack && (
            <details open style={{ marginTop: 16, background: '#1E293B', border: '1px solid #334155', borderRadius: 8, padding: 12 }}>
              <summary style={{ cursor: 'pointer', color: '#FECACA', fontFamily: 'system-ui, sans-serif' }}>Stack trace</summary>
              <pre style={{ marginTop: 8, fontSize: 11, color: '#CBD5E1', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{e.stack}</pre>
            </details>
          )}
          {this.state.info && this.state.info.componentStack && (
            <details style={{ marginTop: 16, background: '#1E293B', border: '1px solid #334155', borderRadius: 8, padding: 12 }}>
              <summary style={{ cursor: 'pointer', color: '#FECACA', fontFamily: 'system-ui, sans-serif' }}>React component stack</summary>
              <pre style={{ marginTop: 8, fontSize: 11, color: '#CBD5E1', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{this.state.info.componentStack}</pre>
            </details>
          )}
          <button onClick={() => window.location.reload()} style={{ marginTop: 16, background: '#0EA5E9', color: 'white', border: 'none', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontFamily: 'system-ui, sans-serif' }}>Reload</button>
        </div>
      </div>
    );
  }
}

// Also catch errors thrown at module load time (e.g. supabase env-var throw
// from src/lib/supabase.js). Without this, the entire bundle fails to
// execute and React never even mounts — yielding the white page that
// hides the real cause. We render the boundary into #root manually with
// the synchronous error so the user sees the actual message.
// FirstPromoter affiliate tracking — loads at boot, captures ?fpr=
// from the URL into localStorage so the SubscribePage can attach it
// to the Stripe Checkout call. No-op when REACT_APP_FIRSTPROMOTER_ACCOUNT
// is unset (local dev).
try { captureFprFromUrl(); initFirstPromoter(); } catch { /* never block boot */ }

try {
  const root = ReactDOM.createRoot(document.getElementById('root'));
  const publicRoute = getPublicRoute();
  if (publicRoute?.kind === 'consent') {
    // Public consent-signing route. Bypass auth providers entirely
    // so a client phone with no session can render and submit.
    root.render(
      <RootErrorBoundary>
        <ConsentSignPage token={publicRoute.token}/>
      </RootErrorBoundary>
    );
  } else if (publicRoute?.kind === 'soa') {
    // Public SOA-signing route. Same model as consent.
    root.render(
      <RootErrorBoundary>
        <SoaSignPage token={publicRoute.token}/>
      </RootErrorBoundary>
    );
  } else if (publicRoute?.kind === 'intake') {
    // Public Take-It-Home QR intake. No auth — the opaque token is the only
    // credential; the page talks solely to /api/public/intake.
    root.render(
      <RootErrorBoundary>
        <IntakePortal token={publicRoute.token}/>
      </RootErrorBoundary>
    );
  } else if (publicRoute?.kind === 'landing') {
    // Public subscriber landing page (/p/<slug>). No auth — renders from the
    // published page JSON and submits to /api/public/page.
    root.render(
      <RootErrorBoundary>
        <LandingPageView slug={publicRoute.token}/>
      </RootErrorBoundary>
    );
  } else if (publicRoute?.kind === 'member') {
    // Carrier portal secure redirect (/member/<token>). No auth — resolves the
    // current official carrier URL and shows a safety interstitial.
    root.render(
      <RootErrorBoundary>
        <MemberRedirect token={publicRoute.token}/>
      </RootErrorBoundary>
    );
  } else {
    root.render(
      <RootErrorBoundary>
        <AuthProvider>
          <SubscriptionProvider>
            <AriaProvider>
              <App />
              <AriaShell/>
            </AriaProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </RootErrorBoundary>
    );
  }
} catch (err) {
  // Last-resort fallback if even the boundary can't mount.
  const rootEl = document.getElementById('root');
  if (rootEl) {
    // AUDIT C8 FIX: escape full HTML-sensitive set, not just <.
    // Previous code escaped only `<` — a `>` in the error message
    // could break the div boundary; ampersands could confuse the
    // parser; single/double quotes weren't relevant here (no
    // attribute context) but included for defense-in-depth.
    const esc = (s) => String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
    rootEl.innerHTML = `
      <div style="min-height:100vh;padding:24px;background:#0F172A;color:#E2E8F0;font-family:ui-monospace,monospace;line-height:1.5">
        <div style="max-width:920px;margin:0 auto">
          <div style="font-size:12px;color:#94A3B8;text-transform:uppercase;letter-spacing:2px">Boot failure</div>
          <h1 style="font-size:22px;color:#FECACA;margin-top:8px;margin-bottom:16px;font-family:system-ui,sans-serif">
            ${esc(err && err.message ? err.message : err)}
          </h1>
          <pre style="background:#1E293B;border:1px solid #334155;border-radius:8px;padding:12px;font-size:11px;color:#CBD5E1;white-space:pre-wrap;word-break:break-all">${esc(err && err.stack ? err.stack : '')}</pre>
        </div>
      </div>
    `;
  }
  // eslint-disable-next-line no-console
  console.error('[boot] Fatal error during initial render:', err);
}
